import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, LogIn, LogOut, LayoutDashboard, PhoneCall } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { BRAND, FEATURES } from '../lib/brand';
import { useAuth } from '../lib/auth';

export default function Header() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);

  const links = [
    { to: '/', label: 'Start' },
    { to: '/unfall-melden', label: 'Unfall melden' },
    ...(FEATURES.pannenhilfe_enabled ? [{ to: '/pannenhilfe', label: 'Pannenhilfe' }] : []),
    { to: '/track', label: 'Status verfolgen' },
  ];

  return (
    <header
      className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur"
      data-testid="site-header"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-3">
        <Link
          to="/"
          className="flex items-center"
          data-testid="site-logo"
          aria-label="UnfallHeld Startseite"
        >
          <img
            src="/brand/logo-light.png"
            alt="UnfallHeld"
            className="h-14 sm:h-16 w-auto object-contain"
            style={{ mixBlendMode: 'multiply' }}
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="px-3 py-2 text-sm text-slate-700 hover:text-[hsl(var(--primary))] hover:bg-slate-100 rounded-lg transition-colors"
              data-testid={`nav-${l.to.replace('/', '') || 'home'}-link`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {/* Desktop-only actions - mobile has the bottom tab bar + hamburger only */}
          <a
            href={`tel:${BRAND.phone}`}
            className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--primary))] hover:underline"
            data-testid="header-call-link"
          >
            <PhoneCall className="h-4 w-4" />
            <span>{BRAND.phoneDisplay}</span>
          </a>
          {user ? (
            <>
              {(user.role === 'admin' || user.role === 'staff') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  data-testid="header-admin-link"
                  className="hidden md:inline-flex"
                >
                  <LayoutDashboard className="h-4 w-4 mr-1.5" /> Admin
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { logout(); navigate('/'); }}
                data-testid="header-logout-button"
                className="hidden md:inline-flex"
              >
                <LogOut className="h-4 w-4 mr-1.5" /> Abmelden
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/login')}
              data-testid="header-login-button"
              className="hidden md:inline-flex"
            >
              <LogIn className="h-4 w-4 mr-1.5" /> Anmelden
            </Button>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="md:hidden"
                data-testid="header-menu-button"
                aria-label="Men\u00fc \u00f6ffnen"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <SheetHeader>
                <SheetTitle className="font-heading text-[hsl(var(--primary))] flex items-center gap-2">
                  <img src="/brand/logo-light.png" alt="UnfallHeld" className="h-8 w-8 object-contain" />
                  {BRAND.name}
                </SheetTitle>
              </SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                {user ? (
                  <>
                    <Link
                      to="/portal"
                      onClick={() => setOpen(false)}
                      className="px-3 py-3 rounded-lg hover:bg-slate-100 text-slate-800 flex items-center gap-2"
                      data-testid="mobile-nav-portal-link"
                    >
                      <LayoutDashboard className="h-4 w-4" /> Mein Portal
                    </Link>
                    {(user.role === 'admin' || user.role === 'staff') && (
                      <Link
                        to="/admin"
                        onClick={() => setOpen(false)}
                        className="px-3 py-3 rounded-lg hover:bg-slate-100 text-slate-800 flex items-center gap-2"
                        data-testid="mobile-nav-admin-link"
                      >
                        <LayoutDashboard className="h-4 w-4" /> Admin Dashboard
                      </Link>
                    )}
                    <button
                      onClick={() => { logout(); setOpen(false); navigate('/'); }}
                      className="text-left px-3 py-3 rounded-lg hover:bg-slate-100 text-red-700 flex items-center gap-2"
                      data-testid="mobile-nav-logout-button"
                    >
                      <LogOut className="h-4 w-4" /> Abmelden
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setOpen(false)}
                      className="px-3 py-3 rounded-lg hover:bg-slate-100 text-slate-800 flex items-center gap-2"
                      data-testid="mobile-nav-login-link"
                    >
                      <LogIn className="h-4 w-4" /> Anmelden
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setOpen(false)}
                      className="px-3 py-3 rounded-lg hover:bg-slate-100 text-slate-800 flex items-center gap-2"
                      data-testid="mobile-nav-register-link"
                    >
                      Konto erstellen
                    </Link>
                  </>
                )}
                <div className="h-px bg-slate-200 my-2" />
                <Link
                  to="/impressum"
                  onClick={() => setOpen(false)}
                  className="px-3 py-3 rounded-lg hover:bg-slate-100 text-slate-600 text-sm"
                  data-testid="mobile-nav-impressum-link"
                >
                  Impressum
                </Link>
                <Link
                  to="/datenschutz"
                  onClick={() => setOpen(false)}
                  className="px-3 py-3 rounded-lg hover:bg-slate-100 text-slate-600 text-sm"
                  data-testid="mobile-nav-datenschutz-link"
                >
                  Datenschutzerkl&auml;rung
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
